import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { getAssetHistory } from '@/api/assets';
import { formatDateTime } from '@/lib/dateFormat';
import type { AssetHistoryEventKind } from '@/types/oscar';

/** What each kind of event is called on screen, in the words somebody working out where a laptop went would use. */
const KIND_LABEL: Record<AssetHistoryEventKind, string> = {
  Assigned: 'Assigned',
  Returned: 'Returned',
  Location: 'Location',
  PrimaryUser: 'Primary user',
  Status: 'Status',
};

/**
 * Where this asset has been and whose it was, as one timeline, newest first.
 *
 * <p>Marty Banfield, 29/10/2026: <i>"Asset location should reflect where a device currently is, with a
 * searchable audit trail of previous locations and users."</i> The assignments, status changes, location
 * changes and Intune primary-user changes were in different places (or, for the last two, not kept at
 * all). This puts them side by side, with a box to narrow it to a person, a place or a word.</p>
 *
 * ⚠ HISTORY OF LOCATION AND PRIMARY USER STARTS WHEN IT WAS TURNED ON. Earlier edits were never recorded
 * as history, so the page says so rather than letting an empty trail read as "it never moved".
 *
 * ⚠ If the tracking table has not been created yet the API says so and this says so, and the assignments
 * and status changes still show.
 */
export function AssetTimelineTab({ assetId }: { assetId: number }) {
  const [filter, setFilter] = useState('');
  const query = useQuery({
    queryKey: ['asset-history', assetId],
    queryFn: ({ signal }) => getAssetHistory(assetId, signal),
  });

  if (query.isLoading) return <p className="text-sm text-darkGrey/70">Loading…</p>;
  if (query.isError || !query.data) {
    return <p role="alert" className="text-sm text-red">Could not load the history. Try again, and if it keeps happening contact Onsite IT Support.</p>;
  }

  const { Events, LocationAndPrimaryUserTracked } = query.data;
  const needle = filter.trim().toLowerCase();
  const shown = needle
    ? Events.filter((e) => [e.Title, e.Detail, e.By, KIND_LABEL[e.Kind]].some((v) => (v ?? '').toLowerCase().includes(needle)))
    : Events;

  return (
    <div className="space-y-3">
      {!LocationAndPrimaryUserTracked && (
        <p role="status" className="flex items-start gap-2 rounded border border-orange/40 bg-orange/10 px-3 py-2 text-sm text-darkGrey">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange" aria-hidden />
          <span>Location and primary-user changes are not being recorded yet, so only assignments and status changes are shown.</span>
        </p>
      )}
      {LocationAndPrimaryUserTracked && (
        <p className="text-xs text-darkGrey/70">
          Location and Intune primary-user changes are recorded from when this was turned on; earlier ones are not here.
        </p>
      )}

      <label className="block text-xs font-semibold uppercase tracking-wide text-darkGrey/70">
        Search this history
        <input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="A person, a place, a ticket…"
          className="mt-1 w-full rounded border border-border px-2 py-1 text-sm font-normal normal-case tracking-normal sm:max-w-sm"
        />
      </label>

      {Events.length === 0 ? (
        <p className="text-sm text-darkGrey/70">Nothing recorded for this asset yet.</p>
      ) : shown.length === 0 ? (
        <p className="text-sm text-darkGrey/70">Nothing in this history matches that.</p>
      ) : (
        <ol className="space-y-2">
          {shown.map((e, i) => (
            <li key={`${e.Kind}-${e.At}-${i}`} className="rounded border border-border bg-white px-3 py-2 text-sm">
              <div className="flex flex-wrap items-baseline gap-x-3">
                <span className="rounded bg-lightBlue/50 px-2 py-0.5 text-xs font-semibold text-navy">{KIND_LABEL[e.Kind]}</span>
                <span className="font-medium text-navy">{e.Title}</span>
              </div>
              {e.Detail && <p className="mt-0.5 text-xs text-darkGrey/80">{e.Detail}</p>}
              <p className="mt-0.5 text-xs text-darkGrey/60">
                {formatDateTime(e.At)}{e.By ? ` · ${e.By}` : ''}
              </p>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
